import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Plug, Cpu, Activity, Search, KeyRound, Terminal } from 'lucide-react';
import { connectYamaha, readStatusYamaha, readDiagYamaha, probeYamaha, hexb, PID_LABELS } from '../lib/yamahaKwp';

// Yamaha YMETER diagnostics (real YDS/YDT protocol). Single-wire K-Line @ 15625 baud via the bridge.
export const YamahaDiag = ({ manager, log, setConnected, setSimulated, useBridge, bridgeUrl, comPort }) => {
  const [busy, setBusy] = useState(false);
  const [yConn, setYConn] = useState(false);
  const [frame, setFrame] = useState(null);
  const [status, setStatus] = useState(null);
  const [diag, setDiag] = useState([]);
  const [probeHex, setProbeHex] = useState('CE');
  const [probeResult, setProbeResult] = useState(null);

  const ensurePort = useCallback(async () => {
    if (!useBridge) throw new Error('Enable "Use native bridge" and run TRMD Engine first');
    log('INFO', `Opening bridge port for Yamaha YMETER @ 15625${comPort ? ` · ${comPort}` : ''}…`);
    await manager.connectBridge({ url: bridgeUrl, baud: 15625, device: comPort || undefined });
    setConnected(true); setSimulated(false);
    if (!manager.bridge || !manager.bridge.yamahaInit)
      throw new Error(`Bridge v${manager.bridgeVersion} is too old — download & run TRMD Engine v1.9.37+`);
    log('SUCCESS', `Native bridge — port open @ 15625.`);
  }, [useBridge, comPort, bridgeUrl, manager, log, setConnected, setSimulated]);

  const doConnect = useCallback(async () => {
    setBusy(true); setYConn(false); setDiag([]); setFrame(null); setStatus(null);
    log('INFO', '=== YAMAHA YDT / YMETER CONNECT (Mio J · 54P0 @ 15625) ===');
    log('INFO', 'Do IG ON → IG OFF → IG ON, leave ignition ON, then this polls FD…');
    try {
      await ensurePort();
      const r = await connectYamaha(manager, { onLog: log, window: 8000 });
      setFrame(r.frame); setYConn(true);
      toast.success('Yamaha connected', { description: `frame ${hexb(r.frame)}` });
    } catch (e) { log('ERROR', `Yamaha connect failed: ${e.message}`); toast.error('Yamaha connect failed', { description: e.message }); }
    finally { setBusy(false); }
  }, [ensurePort, manager, log]);

  const doStatus = useCallback(async () => {
    setBusy(true); log('INFO', 'Reading Yamaha status (CE)…');
    try {
      const r = await readStatusYamaha(manager, { onLog: log });
      setStatus(r);
      if (r.ok) log('SUCCESS', `Status CE → data 0x${r.frame.data.toString(16).toUpperCase()} · b3 0x${r.frame.b3.toString(16).toUpperCase()}`);
      else log('WARN', `No valid CE frame · RX ${r.raw || '(none)'}`);
    } catch (e) { log('ERROR', `Read status failed: ${e.message}`); }
    finally { setBusy(false); }
  }, [manager, log]);

  const doDiag = useCallback(async () => {
    setBusy(true); log('INFO', 'Reading Yamaha live data (PID sweep)…');
    try {
      const rows = await readDiagYamaha(manager, { onLog: log, rounds: 48 });
      setDiag(rows);
      log(rows.length ? 'SUCCESS' : 'WARN', rows.length ? `Read ${rows.length} diagnostic items.` : 'No diagnostic items — enter Diagnosis mode (IG cycle) first.');
    } catch (e) { log('ERROR', `Read diagnostic data failed: ${e.message}`); }
    finally { setBusy(false); }
  }, [manager, log]);

  const doProbe = useCallback(async () => {
    const bytes = (probeHex.match(/[0-9a-fA-F]{2}/g) || []).map((h) => parseInt(h, 16));
    if (!bytes.length) { toast.error('Enter hex bytes, e.g. CA or CB 01'); return; }
    setBusy(true); log('INFO', `Yamaha probe TX ${hexb(bytes)}…`);
    try {
      const r = await probeYamaha(manager, bytes);
      setProbeResult(r);
      log(r.frame ? 'SUCCESS' : 'WARN', `RX ${r.raw || '(none)'}${r.frame ? ` · data 0x${r.frame.data.toString(16).toUpperCase()}` : ''}`);
    } catch (e) { log('ERROR', `Probe failed: ${e.message}`); }
    finally { setBusy(false); }
  }, [probeHex, manager, log]);

  return (
    <div className="ft-panel p-4 flex flex-col gap-3 border-l-2 border-[#E4002B]" data-testid="yamaha-diag-panel">
      <div className="flex items-center justify-between gap-2">
        <span className="ft-label text-[13px] text-[#E4002B] flex items-center gap-1.5 font-bold">
          <Cpu size={16} /> Yamaha YDT / YMETER (K-Line 15625)
        </span>
        <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${yConn ? 'border-[#00E5FF]/50 text-[#00E5FF] bg-[#00E5FF]/10' : 'border-slate-700 text-slate-500'}`} data-testid="yamaha-status">
          {yConn ? 'CONNECTED' : 'not connected'}
        </span>
      </div>

      <div className="rounded-lg border border-[#FFB800]/30 bg-[#FFB800]/5 px-3 py-2 text-[11px] text-[#FFD470] leading-relaxed" data-testid="yamaha-ig-hint">
        <b>Sebelum Connect:</b> putar kunci <b>IG ON → IG OFF → IG ON</b>, biarkan kontak <b>ON</b>, lalu tekan Connect.
        Bridge akan polling byte <span className="font-mono">FD</span> @15625 baud sampai meter/ECU membalas.
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="yamaha-connect-button"
          onClick={doConnect}
          disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-[#E4002B]/10 border border-[#E4002B]/50 text-[#ff5a6e] hover:bg-[#E4002B]/20 disabled:opacity-40 transition"
        >
          <Plug size={15} /> Connect (YDT / Yamaha Meter)
        </button>
        <button
          type="button"
          data-testid="yamaha-status-button"
          onClick={doStatus}
          disabled={busy || !yConn}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-[#00E5FF]/10 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/20 disabled:opacity-40 transition"
        >
          <KeyRound size={15} /> Read Status (CE)
        </button>
        <button
          type="button"
          data-testid="yamaha-diag-button"
          onClick={doDiag}
          disabled={busy || !yConn}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-[#00E5FF]/10 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/20 disabled:opacity-40 transition"
        >
          <Activity size={15} /> Read Diagnostic Data
        </button>
      </div>

      {frame && (
        <div className="text-xs font-mono text-slate-300" data-testid="yamaha-frame">Connect frame: <b className="text-[#00E5FF]">{hexb(frame)}</b></div>
      )}
      {status && (
        <div className="text-xs font-mono text-slate-300 break-all" data-testid="yamaha-status-frame">
          Status (CE): <b className={status.ok ? 'text-[#00E5FF]' : 'text-amber-400'}>{status.ok ? hexb([status.frame.cmd, status.frame.b1, status.frame.b2, status.frame.b3, status.frame.data, status.frame.cs]) : `no frame · RX ${status.raw || '(none)'}`}</b>
        </div>
      )}

      {diag.length > 0 && (
        <div className="rounded-lg border border-[#1E2A42] overflow-hidden" data-testid="yamaha-diag-table">
          <div className="grid grid-cols-[54px_1fr_70px_60px] text-[10px] uppercase tracking-wider text-slate-500 bg-[#0B0F19] px-3 py-1.5">
            <span>PID</span><span>Sensor</span><span>Value</span><span>Hex</span>
          </div>
          {diag.map((r) => (
            <div key={r.idx} className={`grid grid-cols-[54px_1fr_70px_60px] text-xs font-mono px-3 py-1.5 border-t border-[#1E2A42] ${r.valid ? '' : 'opacity-40'}`} data-testid={`yamaha-diag-${r.idx.toString(16)}`}>
              <span className="text-[#E4002B]">P{r.idx.toString(16).toUpperCase().padStart(2, '0')}</span>
              <span className="text-slate-300 truncate">{PID_LABELS[r.idx] || '—'}</span>
              <span className="text-white">{r.val == null ? '—' : r.val}</span>
              <span className="text-slate-500">{r.val == null ? '—' : `0x${r.val.toString(16).toUpperCase().padStart(2, '0')}`}</span>
            </div>
          ))}
        </div>
      )}

      {/* Manual command probe (raw YMETER byte exploration) */}
      <div className="rounded-lg border border-[#1E2A42] bg-[#0b0f14] p-3 flex flex-col gap-2" data-testid="yamaha-probe-box">
        <span className="ft-label text-[10px] text-slate-400 flex items-center gap-1.5"><Terminal size={12} /> Manual command probe (hex)</span>
        <div className="flex items-center gap-2">
          <input
            data-testid="yamaha-probe-input"
            value={probeHex}
            onChange={(e) => setProbeHex(e.target.value.replace(/[^0-9a-fA-F ]/g, '').toUpperCase())}
            placeholder="CE  or  CB 01"
            className="w-32 h-9 rounded-lg bg-[#0b0f14] border border-[#1E2A42] text-slate-200 text-sm font-mono px-2 outline-none focus:border-[#E4002B]"
          />
          <button
            type="button"
            data-testid="yamaha-probe-button"
            onClick={doProbe}
            disabled={busy || !yConn}
            className="h-9 px-3 rounded-lg border border-[#00E5FF]/50 bg-[#00E5FF]/10 text-[#00E5FF] font-bold text-sm inline-flex items-center gap-1.5 hover:bg-[#00E5FF]/20 transition disabled:opacity-40"
          >
            <Search size={14} /> Send
          </button>
          {probeResult && (
            <span className={`text-xs font-mono break-all ${probeResult.frame ? 'text-[#00E5FF]' : 'text-amber-400'}`} data-testid="yamaha-probe-result">{probeResult.raw || '(none)'}</span>
          )}
        </div>
      </div>
    </div>
  );
};
